import { adminReturnPath } from "../functions/adminReturnPath";
import { useEffect, useRef } from "react";
import { useSelector, useDispatch, batch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setUserLogged,
  setUserData,
  setLoading,
  setCorporationData,
  updateCorporationData,
} from "../reducers/userConfigSlice";
import { apiRest } from "../api/constants";
import { getCorporationInfo } from "../api/requests";
import { readLoginLS } from "../functions/rememberedLogin";
import { logIn } from "../api/login";
import { getSalesChannels } from "../api/salesChannels";
import { getCatalogs } from "../api/catalogs";
import { getCategories } from "../api/categories";
import { getCorporationOwner } from "../api/configuration";
import { decodeToken } from "../api/utils";
import { getAttributeGroups } from "../api/attributes";
import { getAlergens } from "../api/products";
import { getOptionGroups } from "../api/options";

const Initializer = () => {
  const navigate = useNavigate();
  const initialTarget=useRef(adminReturnPath(window.location.pathname+window.location.hash));
  const dispatch = useDispatch();
  const { componentActive } = useSelector(
    (state) => state.aplicationConfig.userConfig
  );

  // ✅ Carga de datos de corporación (sin pedir corporationInfo aquí)
  const init = async (corporatioData, userData) => {
    try {
      const [
        responseChannels,
        responseCatalogs,
        responseCategories,
        responseAttributes,
        responseAlergens,
        responseOptions,
        responseCorporationOwner,
      ] = await Promise.all([
        getSalesChannels(),
        getCatalogs(),
        getCategories(),
        getAttributeGroups(),
        getAlergens(),
        getOptionGroups(),
        getCorporationOwner(),
      ]);

      // Batching: agrupa varios dispatch en un solo ciclo de render
      batch(() => {
        dispatch(setCorporationData(corporatioData));
        dispatch(setUserData(userData));
        dispatch(setUserLogged(true));
        if (responseChannels && !responseChannels.error) {
          dispatch(
            updateCorporationData({
              key: "channels",
              data: responseChannels.data,
            })
          );
        }
        if (responseCatalogs && !responseCatalogs.error) {
          dispatch(
            updateCorporationData({
              key: "catalogs",
              data: responseCatalogs.data,
            })
          );
        }
        if (responseCategories && !responseCategories.error) {
          dispatch(
            updateCorporationData({
              key: "categories",
              data: responseCategories.data,
            })
          );
        }
        if (responseAttributes && !responseAttributes.error) {
          dispatch(
            updateCorporationData({
              key: "attributes",
              data: responseAttributes.data,
            })
          );
        }
        if (responseAlergens && !responseAlergens.error) {
          dispatch(
            updateCorporationData({
              key: "alergens",
              data: responseAlergens.data,
            })
          );
        }
        if (responseOptions && !responseOptions.error) {
          dispatch(
            updateCorporationData({
              key: "options",
              data: responseOptions.data,
            })
          );
        }
        if (responseCorporationOwner && !responseCorporationOwner.error) {
          dispatch(
            updateCorporationData({
              key: "corporationOwner",
              data: responseCorporationOwner.data,
            })
          );
        }
      });
      return true;
    } catch (err) {
      console.error("[init] Error general al inicializar datos:", err);
      return false;
    }
  };

  const autoLogin = async (corporationData) => {
    const savedLogin = readLoginLS(apiRest);
    if (savedLogin?.email && savedLogin?.password) {
      dispatch(setLoading(true));
      const data = { email: savedLogin.email, password: savedLogin.password };
      const response = await logIn(data);
      if (response && !response.error) {
        const responseDecoded = decodeToken(response.data.data);

        const success = await init(corporationData, responseDecoded);
        if (success) {
          navigate(initialTarget.current,{replace:true});
          dispatch(setLoading(false));
        }
      } else {
        dispatch(setUserLogged(false));
        dispatch(setLoading(false));
      }
    } else {
      dispatch(setUserLogged(false));
    }
  };

  function setFavicon(corporationId) {
    const url = `https://cdn.delibran.app/corporations/${corporationId}/configuration/favicon.png`;
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }

    // Truco anti-cache
    link.href = `${url}?v=${Date.now()}`;
  }

  // ✅ Efecto principal
  useEffect(() => {
    const initializeApp = async () => {
      console.log("| 🚀 Inicializando aplicación...");

      try {
        // 1) PRIMERA LLAMADA: getCorporationInfo
        const corporationRes = await getCorporationInfo();

        // 2) Si la plataforma no existe -> redirección y cortar ejecución
        if (
          corporationRes?.error === true &&
          corporationRes?.message === "Platform not found."
        ) {
          window.location.assign("https://delibran.com/");
          return; // Importante: no seguimos
        } else {
          // 3) Autologin + datos
          dispatch(setCorporationData(corporationRes.data));
          console.log(
            "SET FAV: ",
            corporationRes.data.corporation.corporationId
          );
          setFavicon(corporationRes.data.corporation.corporationId);
          autoLogin(corporationRes.data);
        }
      } catch (e) {
        console.error("[initializeApp] Error en inicialización:", e);
      } finally {
        // Si hubo redirección, no se ejecuta; si no, cerramos loading y disparamos onInit
      }
    };

    initializeApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Scroll al cargar cada componente activo
  useEffect(() => {
    document.getElementById("adminBody")?.scrollTo(0, 0);
  }, [componentActive]);

  return null;
};

export default Initializer;
